/**
 * Iter-158.13.2.20f.42-char-workspace — BulkSceneEdit (H&M Bulk
 * Edit Scenes). Ships the "Scenes → Bulk Edit Scenes → per-Scene
 * editable continuity workspace" flow using the existing
 * per-field auto-save-on-blur pattern proven by BulkCastEdit.
 *
 *   - Scene-level fields (heading / description / look / notes)
 *     are editable via the shared <EditableField> component and
 *     the existing PATCH /api/projects/{pid}/scenes/{sid} endpoint.
 *   - Each Scene is expandable. When expanded, the drill-down
 *     renders per-Character blocks fed by the existing
 *     GET .../scenes/{sid}/continuity endpoint. Each block hosts:
 *       (1) an editable H&M Continuity field bound to the existing
 *           PUT .../scenes/{sid}/characters/{cid}/notes endpoint
 *           (writes the same character_scene_states record surfaced
 *            on SceneDetail / CharacterDetail), and
 *       (2) an independently collapsible "AI Hair & Makeup
 *           Breakdown" accordion that, when expanded, mounts the
 *           EXISTING <DeptBreakdownSection> with scope
 *           `{scene_id, character_id}`. Same records; edit/delete/
 *           add via the same existing endpoints; ZERO duplication.
 *   - Scene-wide unassigned AI findings (entries whose
 *     character_ids array is empty) remain visible in a separate,
 *     independently collapsed section rendered AFTER the character
 *     list. Read-only here to avoid duplicating character-scoped
 *     entries — the AI Breakdown source of truth for scoped entries
 *     is each character's own accordion above.
 *   - apiBase is drawn from useApiBase() (workspace context) so
 *     under BetaHmWorkspace the URL is /beta/hm-app/projects, and
 *     under canonical /projects the URL is /projects — preserving
 *     the .20f.42c workspace-context repair.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronDown, ChevronRight, ClapperboardIcon, AlertCircle } from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ProjectHeader from "../components/ProjectHeader";
import CharacterSceneAIBreakdown from "../components/CharacterSceneAIBreakdown";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { EditableField } from "./BulkCastEdit";


const SORT_KEYS = ["scene_number", "episode"];
const sortLabels = { scene_number: "Scene", episode: "Episode" };


function sortScenes(scenes, key) {
    // Deterministic order that matches ScenesHub / OnSetMode:
    // primary sort is always order_index (canonical script order),
    // then episode/scene_number as a tiebreaker per key. The two
    // toggles differ only in how they LABEL the grouping — the
    // underlying order stays script-canonical either way.
    const arr = [...scenes];
    if (key === "episode") {
        arr.sort((a, b) => {
            const ea = (a.episode || "").toString();
            const eb = (b.episode || "").toString();
            if (ea !== eb) return ea.localeCompare(eb, undefined, { numeric: true });
            return (a.order_index || 0) - (b.order_index || 0);
        });
    } else {
        arr.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    }
    return arr;
}


function CharacterBlock({ apiBase, projectId, sceneId, block, onNotesSaved }) {
    // Iter-158.13.2.20f.42-relational — editable H&M continuity
    // (same character_scene_states record used everywhere) plus the
    // shared <CharacterSceneAIBreakdown> accordion (mounts the
    // existing DeptBreakdownSection scoped to this exact (scene,
    // character) intersection). Zero duplicate records; identical
    // presentation to Scene Detail and Character Detail per the
    // .20f.42-relational consistency lock.
    const c = block?.character || {};
    const characterId = c.id;

    const saveContinuity = useCallback(
        async (next) => {
            const { data } = await api.put(
                `${apiBase}/${projectId}/scenes/${sceneId}/characters/${characterId}/notes`,
                { notes: next },
            );
            // Bubble the fresh notes shape so the drill-down keeps
            // updated_at / updated_by_name in sync without a full
            // refetch — matches the ContinuityBlock semantics.
            onNotesSaved?.(characterId, data);
        },
        [apiBase, projectId, sceneId, characterId, onNotesSaved],
    );

    return (
        <li
            data-testid={`bulk-scenes-char-block-${sceneId}-${characterId}`}
            className="rounded-md border border-white/5 bg-black/30 p-2.5 space-y-2"
        >
            <div className="flex items-baseline gap-2">
                <span
                    data-testid={`bulk-scenes-char-name-${sceneId}-${characterId}`}
                    className="font-semibold text-white text-[13px]"
                >
                    {c.name || "Character"}
                </span>
                {c.character_number ? (
                    <span className="text-[10px] text-neutral-400 font-mono">
                        #{c.character_number}
                    </span>
                ) : null}
                {c.team === "crowd" ? (
                    <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-200">
                        Crowd
                    </span>
                ) : null}
            </div>

            <div>
                <label
                    className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1"
                    data-testid={`bulk-scenes-char-continuity-label-${sceneId}-${characterId}`}
                >
                    H&amp;M Continuity
                </label>
                <EditableField
                    testid={`bulk-scenes-char-continuity-${sceneId}-${characterId}`}
                    value={block?.notes || ""}
                    placeholder="Hair, makeup, costume notes for this character in this scene…"
                    multiline
                    maxLength={2000}
                    onSave={saveContinuity}
                />
            </div>

            <CharacterSceneAIBreakdown
                apiBase={apiBase}
                projectId={projectId}
                sceneId={sceneId}
                characterId={characterId}
                testidPrefix="bulk-scenes-char-ai"
            />
        </li>
    );
}


function SceneDrilldown({ apiBase, projectId, sceneId }) {
    // Iter-158.13.2.20f.42-char-workspace — the drill-down now hosts
    // editable Character-in-Scene H&M continuity + a per-character
    // AI Breakdown accordion (see CharacterBlock above). Scene-wide
    // unassigned findings (character_ids empty) remain visible in a
    // separate collapsed section rendered after the character list.
    // Read-only there to avoid duplicating character-scoped entries;
    // the source of truth for scoped entries is each character's own
    // AI Breakdown accordion.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [blocks, setBlocks] = useState([]);
    const [findings, setFindings] = useState([]);
    const [sceneWideOpen, setSceneWideOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [{ data: cont }, findingsRes] = await Promise.all([
                    api.get(`${apiBase}/${projectId}/scenes/${sceneId}/continuity`),
                    api
                        .get(`${apiBase}/${projectId}/hm-breakdown-entries`, {
                            params: { scene_id: sceneId },
                        })
                        .catch((e) => ({ data: [], _error: formatApiError(e) })),
                ]);
                if (!alive) return;
                setBlocks(Array.isArray(cont?.characters) ? cont.characters : []);
                setFindings(Array.isArray(findingsRes?.data) ? findingsRes.data : []);
                if (findingsRes?._error) {
                    // Non-fatal — continuity still renders.
                    setError("");
                }
            } catch (e) {
                if (alive) setError(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [apiBase, projectId, sceneId]);

    // Scene-wide findings = entries with an empty character_ids
    // array. Character-scoped entries are surfaced inside each
    // character's own AI accordion (fetched independently by
    // DeptBreakdownSection), so this bucket exists strictly to keep
    // legitimately Scene-only findings visible without hiding them.
    const sceneWideFindings = useMemo(
        () =>
            findings.filter(
                (f) => !Array.isArray(f.character_ids) || f.character_ids.length === 0,
            ),
        [findings],
    );

    const onNotesSaved = useCallback((characterId, next) => {
        setBlocks((prev) =>
            prev.map((b) =>
                b?.character?.id === characterId
                    ? {
                          ...b,
                          notes: next?.notes ?? "",
                          notes_updated_at: next?.notes_updated_at ?? null,
                          notes_updated_by_name: next?.notes_updated_by_name ?? null,
                      }
                    : b,
            ),
        );
    }, []);

    if (loading) {
        return (
            <div
                data-testid={`bulk-scenes-drilldown-loading-${sceneId}`}
                className="mt-3 h-16 rounded-lg border border-white/5 bg-neutral-900/40 animate-pulse"
            />
        );
    }

    if (error) {
        return (
            <p
                data-testid={`bulk-scenes-drilldown-error-${sceneId}`}
                className="mt-3 text-[11px] text-red-300"
            >
                {error}
            </p>
        );
    }

    return (
        <div
            data-testid={`bulk-scenes-drilldown-${sceneId}`}
            className="mt-4 rounded-lg border border-white/5 bg-neutral-900/40 p-3 space-y-3"
        >
            <div className="flex items-center gap-2">
                <ClapperboardIcon size={12} className="text-neutral-400" />
                <p className="text-[10px] uppercase tracking-[0.24em] text-neutral-400">
                    Characters in this Scene
                </p>
            </div>

            {blocks.length === 0 ? (
                <p
                    data-testid={`bulk-scenes-no-characters-${sceneId}`}
                    className="text-[11px] text-neutral-500"
                >
                    No characters attached to this scene.
                </p>
            ) : (
                <ul className="space-y-2">
                    {blocks.map((b) => (
                        <CharacterBlock
                            key={b?.character?.id}
                            apiBase={apiBase}
                            projectId={projectId}
                            sceneId={sceneId}
                            block={b}
                            onNotesSaved={onNotesSaved}
                        />
                    ))}
                </ul>
            )}

            {sceneWideFindings.length > 0 ? (
                <div
                    data-testid={`bulk-scenes-wide-findings-${sceneId}`}
                    className="rounded-md border border-white/5 bg-black/30"
                >
                    <button
                        type="button"
                        data-testid={`bulk-scenes-wide-findings-toggle-${sceneId}`}
                        aria-expanded={sceneWideOpen}
                        onClick={() => setSceneWideOpen((prev) => !prev)}
                        className="tap w-full flex items-center justify-between gap-2 px-2.5 py-2 text-left text-[10px] uppercase tracking-[0.24em] text-primary/80 font-semibold hover:bg-white/[0.03]"
                        title={sceneWideOpen ? "Collapse Scene-wide findings" : "Expand Scene-wide findings"}
                    >
                        <span>Scene-wide AI Hair &amp; Makeup Breakdown</span>
                        {sceneWideOpen ? (
                            <ChevronDown size={12} className="text-primary/80" />
                        ) : (
                            <ChevronRight size={12} className="text-primary/80" />
                        )}
                    </button>
                    {sceneWideOpen ? (
                        <ul
                            data-testid={`bulk-scenes-wide-findings-body-${sceneId}`}
                            className="px-2.5 pb-2.5 pt-1 space-y-1.5"
                        >
                            {sceneWideFindings.map((f) => (
                                <li
                                    key={f.id}
                                    data-testid={`bulk-scenes-wide-finding-${sceneId}-${f.id}`}
                                    className="text-[12px] text-neutral-200"
                                >
                                    {f.title ? (
                                        <span className="text-neutral-400 mr-1">
                                            {f.title}:
                                        </span>
                                    ) : null}
                                    <span className="whitespace-pre-wrap">{f.body}</span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}


export default function BulkSceneEdit() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    const [project, setProject] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [sort, setSort] = useState("scene_number");
    const [filter, setFilter] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [{ data: p }, { data: sc }, { data: perms }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    api.get(`${apiBase}/${projectId}/scenes`),
                    api
                        .get(`${apiBase}/${projectId}/permissions/me`)
                        .catch(() => ({ data: { permissions: {} } })),
                ]);
                if (!alive) return;
                const role = p?.role;
                const canEdit =
                    role === "owner" ||
                    role === "admin" ||
                    Boolean(perms?.permissions?.scenes_edit);
                if (!canEdit) {
                    toast.error("You don't have permission to bulk-edit scenes.");
                    navigate(`${routeBase}/${projectId}/scenes`, { replace: true });
                    return;
                }
                setProject(p);
                setScenes(Array.isArray(sc) ? sc : []);
            } catch (e) {
                if (alive) setError(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [projectId, navigate, apiBase, routeBase]);

    const visible = useMemo(() => {
        const sorted = sortScenes(scenes, sort);
        const needle = filter.trim().toLowerCase();
        if (!needle) return sorted;
        return sorted.filter((s) => {
            const hay = [
                s.heading,
                s.scene_number,
                s.episode,
                s.look,
                s.notes,
                s.description,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(needle);
        });
    }, [scenes, sort, filter]);

    // Iter-158.13.2 — surgical PATCH: only the exact edited field
    // is sent. No broad replacement payloads. Preserves the human-
    // authority invariant (unrelated data is never touched).
    const updateField = useCallback(
        async (id, patch) => {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/scenes/${id}`,
                patch,
            );
            setScenes((prev) =>
                prev.map((s) => (s.id === id ? { ...s, ...data } : s)),
            );
        },
        [apiBase, projectId],
    );

    const toggleExpanded = useCallback((id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    return (
        <div data-testid="bulk-scenes-page" className="min-h-screen">
            <ProjectHeader
                projectName={project?.name}
                subtitle="Bulk edit scenes"
            />

            <main className="max-w-2xl mx-auto px-5 pt-5 pb-32 w-full">
                <Link
                    data-testid="bulk-scenes-back-link"
                    to={`${routeBase}/${projectId}/scenes`}
                    className="tap inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-4"
                >
                    <ArrowLeft size={14} />
                    Back to Scenes
                </Link>

                <div className="flex items-start gap-3 mb-5">
                    <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                        <ClapperboardIcon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1
                            data-testid="bulk-scenes-title"
                            className="font-display text-2xl sm:text-3xl font-black text-white leading-none"
                        >
                            Bulk edit scenes
                        </h1>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">
                            <span data-testid="bulk-scenes-count">
                                {scenes.length} scenes
                            </span>
                        </p>
                    </div>
                    <Button
                        type="button"
                        data-testid="bulk-scenes-save-top"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/scenes`, {
                                state: { lens: "onset" },
                            })
                        }
                        className="shrink-0 h-10 px-4 font-semibold"
                    >
                        <Check size={14} className="mr-1.5" />
                        Save
                    </Button>
                </div>

                <p className="rounded-md border border-emerald-400/20 bg-emerald-400/5 text-emerald-200/80 text-[11px] px-3 py-2 mb-4 leading-relaxed">
                    Each Scene field auto-saves the moment you blur it (or press
                    <kbd className="font-mono mx-1 px-1 rounded bg-secondary text-emerald-100 border border-white/10">Enter</kbd>).
                    Expand any Scene to edit each Character&rsquo;s H&amp;M
                    Continuity and open its AI Hair &amp; Makeup Breakdown
                    inline. Tap <strong className="font-semibold">Save</strong>
                    when you&rsquo;re done to return to Scenes.
                </p>

                {error ? (
                    <p
                        data-testid="bulk-scenes-error"
                        className="rounded-md border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-3 py-2 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                <div className="sticky top-14 z-30 -mx-4 px-4 py-2.5 bg-background/95 backdrop-blur border-b border-white/10 mb-3">
                    <div className="flex items-center gap-2">
                        <Input
                            data-testid="bulk-scenes-filter"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by heading, look, notes or number"
                            className="h-10 bg-secondary border-transparent text-sm"
                        />
                        <div className="flex items-center rounded-md bg-secondary border border-white/10 overflow-hidden shrink-0">
                            {SORT_KEYS.map((k, i) => (
                                <button
                                    key={k}
                                    type="button"
                                    data-testid={`bulk-scenes-sort-${k}`}
                                    onClick={() => setSort(k)}
                                    className={[
                                        "h-10 px-3 text-[11px] font-semibold uppercase tracking-wider transition-colors",
                                        sort === k
                                            ? "bg-primary text-primary-foreground"
                                            : "text-neutral-300 hover:bg-white/10",
                                        i > 0 ? "border-l border-white/10" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                >
                                    {sortLabels[k]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p className="mt-1.5 text-[11px] text-neutral-500">
                        Tap any Scene field to edit. Changes save when you blur
                        the field or press <kbd className="font-mono">Enter</kbd>.
                    </p>
                </div>

                {loading ? (
                    <div data-testid="bulk-scenes-loading" className="space-y-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-32 rounded-xl border border-white/10 bg-card animate-pulse"
                            />
                        ))}
                    </div>
                ) : scenes.length === 0 ? (
                    <div
                        data-testid="bulk-scenes-empty"
                        className="rounded-xl border border-dashed border-white/10 p-10 text-center"
                    >
                        <ClapperboardIcon
                            size={28}
                            className="mx-auto text-neutral-500"
                        />
                        <p className="mt-3 text-sm text-neutral-300">No scenes yet.</p>
                        <p className="text-xs text-neutral-500 mt-1">
                            Import a script in Settings → Script setup, or add
                            scenes from the Scenes hub.
                        </p>
                    </div>
                ) : visible.length === 0 ? (
                    <div
                        data-testid="bulk-scenes-no-matches"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <AlertCircle
                            size={20}
                            className="mx-auto text-neutral-500"
                        />
                        <p className="mt-2 text-sm text-neutral-400">
                            No scenes match &ldquo;{filter}&rdquo;.
                        </p>
                    </div>
                ) : (
                    <ul data-testid="bulk-scenes-list" className="space-y-2">
                        {visible.map((s) => {
                            const isOpen = expanded.has(s.id);
                            const label = [
                                s.episode ? `Ep ${s.episode}` : null,
                                s.scene_number ? `Sc ${s.scene_number}` : null,
                            ]
                                .filter(Boolean)
                                .join(" · ");
                            return (
                                <li
                                    key={s.id}
                                    data-testid={`bulk-scenes-row-${s.id}`}
                                    className="rounded-xl border border-white/10 bg-card p-3 sm:p-4"
                                >
                                    <div className="flex items-start gap-2 mb-2">
                                        <button
                                            type="button"
                                            data-testid={`bulk-scenes-expand-${s.id}`}
                                            aria-expanded={isOpen}
                                            onClick={() => toggleExpanded(s.id)}
                                            className="tap shrink-0 h-8 w-8 rounded-md border border-white/10 flex items-center justify-center text-neutral-300 hover:bg-white/5"
                                            title={isOpen ? "Collapse" : "Expand to edit characters + AI findings"}
                                        >
                                            {isOpen ? (
                                                <ChevronDown size={16} />
                                            ) : (
                                                <ChevronRight size={16} />
                                            )}
                                        </button>
                                        <div className="min-w-0 flex-1">
                                            <p
                                                data-testid={`bulk-scenes-label-${s.id}`}
                                                className="text-[10px] uppercase tracking-[0.24em] text-neutral-500"
                                            >
                                                {label || "Scene"}
                                                {s.omitted ? (
                                                    <span className="ml-2 text-amber-300/80">· omitted</span>
                                                ) : null}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                                                Heading
                                            </label>
                                            <EditableField
                                                testid={`bulk-scenes-heading-${s.id}`}
                                                value={s.heading}
                                                placeholder="INT. LOCATION — TIME"
                                                maxLength={240}
                                                onSave={(next) =>
                                                    updateField(s.id, { heading: next })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                                                Description
                                            </label>
                                            <EditableField
                                                testid={`bulk-scenes-description-${s.id}`}
                                                value={s.description}
                                                placeholder="Scene description"
                                                multiline
                                                maxLength={2000}
                                                onSave={(next) =>
                                                    updateField(s.id, { description: next })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                                                Look
                                            </label>
                                            <EditableField
                                                testid={`bulk-scenes-look-${s.id}`}
                                                value={s.look}
                                                placeholder='Short label (e.g. "Rain", "Aftermath")'
                                                maxLength={60}
                                                onSave={(next) =>
                                                    updateField(s.id, { look: next })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                                                Notes
                                            </label>
                                            <EditableField
                                                testid={`bulk-scenes-notes-${s.id}`}
                                                value={s.notes}
                                                placeholder="Free-form Scene notes"
                                                multiline
                                                maxLength={2000}
                                                onSave={(next) =>
                                                    updateField(s.id, { notes: next })
                                                }
                                            />
                                        </div>
                                    </div>

                                    {isOpen ? (
                                        <SceneDrilldown
                                            apiBase={apiBase}
                                            projectId={projectId}
                                            sceneId={s.id}
                                        />
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}

                <div className="mt-6 text-center">
                    <Button
                        type="button"
                        data-testid="bulk-scenes-save-bottom"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/scenes`, {
                                state: { lens: "onset" },
                            })
                        }
                        className="min-tap font-semibold"
                    >
                        <Check size={14} className="mr-1.5" />
                        Save changes
                    </Button>
                </div>
            </main>
        </div>
    );
}
